import { Component, OnInit, signal, inject, computed } from '@angular/core';
import { debounceTime } from 'rxjs';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { ConfirmService } from '../../../Utils/_services/confirmation-dialog.service';
import { ToastrService } from 'ngx-toastr';
import { EventService } from '../../../Utils/_services/admin/event/event.service';
import { IEvent, IGetEventsRequest } from '../../../Utils/_models/event.model';
import { SpinnerLoaderComponent } from '../../../shared/components/spinner-loader/spinner-loader.component';

// View model - extends IEvent with computed display fields
export interface IEventView extends IEvent {
  initials: string;
  avatarBg: string;
  selected?: boolean;
}

@Component({
  selector: 'app-events',
  standalone: true,
  imports: [
     SpinnerLoaderComponent,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SelectModule,
    InputTextModule,
    ButtonModule,
    TagModule
   
  ],
  templateUrl: './events.component.html',
  styleUrls: ['./events.component.css'],
})
export class EventsComponent implements OnInit {
  private eventService = inject(EventService);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private confirmationService = inject(ConfirmService);
  private toastr = inject(ToastrService);

  filterForm: FormGroup = this.fb.group({
    status: [null],
    search: [''],
  });


  statusOptions = [
    { label: 'All', value: null },
    { label: 'Active', value: 'active' },
    { label: 'Completed', value: 'completed' },
    { label: 'Deleted', value: 'cancelled' },
  ];

  events = signal<IEventView[]>([]);
  totalCount = signal(0);
  hasMore = signal(false);
  isLoading = signal(false);
  error = signal<string | null>(null);

  allSelected = signal(false);
  hasSelection = signal(false);
  includeDeleted = signal(false);

  pageSize = signal(5);
  pageSizes = [5, 10, 20, 50];
  currentPage = signal(1);

  paginatedEvents = computed(() => this.events());

  get totalPages() {
    return Math.max(1, Math.ceil(this.totalCount() / this.pageSize()));
  }
  get showingFrom() {
    return this.totalCount() === 0
      ? 0
      : (this.currentPage() - 1) * this.pageSize() + 1;
  }
  get showingTo() {
    return Math.min(this.currentPage() * this.pageSize(), this.totalCount());
  }

  ngOnInit(): void {
    this.loadEvents();
    this.filterForm.valueChanges.pipe(debounceTime(400)).subscribe(() => {
      this.applyFilters();
    });
  }

  loadEvents(): void {
    this.isLoading.set(true);
    this.error.set(null);

    const { status, search } = this.filterForm.value;
    const req: IGetEventsRequest = {
      page: this.currentPage(),
      limit: this.pageSize(),
      status: status ?? undefined,
      search: search?.trim() || undefined,
    };

    this.eventService.getEvents(req).subscribe({
      next: (res) => {
        const mapped: IEventView[] = res.data.events.map((e) => ({
          ...e,
          initials: this.getHostInitials(e.host?.name ?? ''),
          avatarBg: this.getAvatarBg(e.host?.name ?? ''),
          selected: false,
        }));
        this.events.set(mapped);
        this.totalCount.set(res.data.totalCount);
        this.hasMore.set(res.data.hasMore);
        this.isLoading.set(false);
        this.allSelected.set(false);
        this.hasSelection.set(false);
      },
      error: () => {
        this.error.set('Failed to load events. Please try again.');
        this.isLoading.set(false);
      },
    });
  }

  applyFilters(): void {
    this.currentPage.set(1);
    this.loadEvents();
  }

  toggleIncludeDeleted(): void {
    this.includeDeleted.update(v => !v);
    this.applyFilters();
  }

  viewEvent(event: IEventView): void {
    this.router.navigate(['/admin/events', event._id]);
  }

  async deleteEvent(event: IEventView, $event: MouseEvent) {
    $event.stopPropagation();
    
    const confirmed = await this.confirmationService.confirm({
      header: 'Delete Event',
      bodyHeader: 'Are you sure?',
      bodySubtitle: `You are about to delete "${event.title}".`,
      yesButtonText: 'Delete',
      yesBtnCss: 'btn-danger',
      noBtnCss: "btn-secondary",
      noButtonText: "Cancel",
      width: "500px",
    });

    if (confirmed) {
      this.eventService.deleteEvent(event._id).subscribe({
        next: () => {
          this.toastr.success(`"${event.title}" has been deleted.`, 'Deleted');
          this.loadEvents();
        },
        error: () => {
          this.toastr.error('Failed to delete event.', 'Error');
        },
      });
    }
  }

  toggleSelectAll(): void {
    this.allSelected.update(v => !v);
    this.events().forEach(e => {
      if (e.status !== 'cancelled' && e.status !== 'Cancelled' && e.status !== 'Deleted' && e.status !== 'deleted') {
        e.selected = this.allSelected();
      }
    });
    this.updateSelectAllState();
  }

  updateSelectAllState(): void {
    const selectable = this.events().filter(e => e.status !== 'cancelled' && e.status !== 'Cancelled' && e.status !== 'Deleted' && e.status !== 'deleted');
    const selectedCount = selectable.filter(e => e.selected).length;
    this.allSelected.set(
      selectedCount === selectable.length && selectable.length > 0
    );
    this.hasSelection.set(selectedCount > 0);
  }

  async deleteSelectedEvents() {
    const selectedIds = this.events().filter(e => e.selected).map(e => e._id);
    if (selectedIds.length === 0) return;

    const confirmed = await this.confirmationService.confirm({
      header: 'Delete Selected Events',
      bodyHeader: 'Are you sure?',
      bodySubtitle: `You are about to delete ${selectedIds.length} selected event(s).`,
      yesButtonText: 'Delete',
      yesBtnCss: 'btn-danger',
      noBtnCss: "btn-secondary",
      noButtonText: "Cancel",
      width: "500px",
    });

    if (confirmed) {
      this.isLoading.set(true);
      this.eventService.bulkDeleteEvents(selectedIds).subscribe({
        next: () => {
          this.toastr.success(`${selectedIds.length} event(s) have been deleted.`, 'Deleted');
          this.loadEvents();
        },
        error: () => {
          this.toastr.error('Failed to delete events.', 'Error');
          this.isLoading.set(false);
        },
      });
    }
  }

  onPageSizeChange(newSize: any) {
    this.pageSize.set(Number(newSize));
    this.currentPage.set(1);
    this.loadEvents();
  }

  prevPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.update((v) => v - 1);
      this.loadEvents();
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages) {
      this.currentPage.update((v) => v + 1);
      this.loadEvents();
    }
  }

  getHostInitials(name: string): string {
    return name
      .split(' ')
      .map((n) => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  private getAvatarBg(name: string): string {
    const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6'];
    const index =
      name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % colors.length;
    return colors[index];
  }
}